import { useMemo, useState, useEffect } from 'react';

import Tabs, { Tab } from '~/shared/components/Tabs';
import Typography from '~/shared/components/Typography';
import SearchIcon from '~/shared/assets/icons/search.svg';

import { Condition } from '../useAutoscaleRulesBuilder';

import { useDryRunMutation } from './hooks/useDryRunMutation';
import VolumesTable from './VolumesTable';

import type { Volume } from '~/shared/types/api';
import type { AutoscaleVolume } from './columnsDefs';

function mapToAutoscaleVolume(volume: Volume): AutoscaleVolume {
  return {
    volumeId: volume.volumeId ?? '',
    cluster: volume.clusterName ?? '',
    nodeGroup: volume.nodeGroupName ?? '',
    instanceId: volume.instanceId ?? '',
    tags: volume.tags ?? [],
    datafyDiskSize: volume.diskSize ?? 0,
    supported: volume.isDatafiable ?? null,
  };
}

type TabValue = 'datafiable' | 'potentiallyDatafiable' | 'excluded';

export default function CreateAutoscaleRulesTables({ conditions }: { conditions: Condition[] }) {
  const [activeTab, setActiveTab] = useState<TabValue>('datafiable');
  const [pageOffset, setPageOffset] = useState<string>('0');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const dryRunMutation = useDryRunMutation();

  useEffect(() => {
    const hasValidConditions = conditions.some(
      (condition) =>
        condition.type &&
        condition.conditionOperators.some(
          (conditionOperator) => conditionOperator.condition && conditionOperator.value.length > 0
        )
    );

    if (hasValidConditions) {
      dryRunMutation.mutate(conditions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditions]);

  const { datafiable, potentiallyDatafiable, excluded } = useMemo(() => {
    if (!dryRunMutation.data) {
      return {
        datafiable: { volumes: [], count: 0, totalCapacity: 0, averageUtilization: 0 },
        potentiallyDatafiable: { volumes: [], count: 0, totalCapacity: 0, averageUtilization: 0 },
        excluded: { volumes: [], count: 0, totalCapacity: 0, averageUtilization: 0 },
      };
    }

    const filterVolumes = (volumes: Volume[]) => {
      const mappedVolumes = volumes.map(mapToAutoscaleVolume);

      if (!searchQuery.trim()) {
        return mappedVolumes;
      }

      const query = searchQuery.toLowerCase();
      return mappedVolumes.filter((volume) => {
        // Search in volumeId, cluster, nodeGroup, instanceId
        if (
          volume.volumeId.toLowerCase().includes(query) ||
          volume.cluster.toLowerCase().includes(query) ||
          volume.nodeGroup.toLowerCase().includes(query) ||
          volume.instanceId.toLowerCase().includes(query)
        ) {
          return true;
        }

        // Search in tags
        return volume.tags.some(
          (tag) => tag.key.toLowerCase().includes(query) || tag.value.toLowerCase().includes(query)
        );
      });
    };

    const datafiableFiltered = filterVolumes(dryRunMutation.data.datafiable.volumes);
    const potentiallyDatafiableFiltered = filterVolumes(
      dryRunMutation.data.potentiallyDatafiable.volumes
    );
    const excludedFiltered = filterVolumes(dryRunMutation.data.excluded.volumes);

    return {
      datafiable: {
        ...dryRunMutation.data.datafiable,
        volumes: datafiableFiltered,
        count: datafiableFiltered.length,
      },
      potentiallyDatafiable: {
        ...dryRunMutation.data.potentiallyDatafiable,
        volumes: potentiallyDatafiableFiltered,
        count: potentiallyDatafiableFiltered.length,
      },
      excluded: {
        ...dryRunMutation.data.excluded,
        volumes: excludedFiltered,
        count: excludedFiltered.length,
      },
    };
  }, [dryRunMutation.data, searchQuery]);

  const tabs: Tab<TabValue>[] = [
    {
      label: 'Matching volumes',
      value: 'datafiable',
      right: (
        <span className="bg-bg px-1.5 py-1 text-xs text-gray-700 rounded-[4px] font-family-inter font-[700]">
          {datafiable.count}
        </span>
      ),
    },
    {
      label: 'Potential volumes',
      value: 'potentiallyDatafiable',
      right: (
        <span className="bg-bg px-1.5 py-1 text-xs text-gray-700 rounded-[4px] font-family-inter font-[700]">
          {potentiallyDatafiable.count}
        </span>
      ),
    },
    {
      label: 'Exceptions',
      value: 'excluded',
      right: (
        <span className="bg-bg px-1.5 py-1 text-xs text-gray-700 rounded-[4px] font-family-inter font-[700]">
          {excluded.count}
        </span>
      ),
    },
  ];

  const currentCategory =
    activeTab === 'datafiable'
      ? datafiable
      : activeTab === 'potentiallyDatafiable'
        ? potentiallyDatafiable
        : excluded;
  const currentVolumes: AutoscaleVolume[] = currentCategory.volumes;
  const currentCount = currentCategory.count;

  useEffect(() => {
    setPageOffset('0');
  }, [searchQuery, activeTab]);

  return (
    <div className="flex flex-col h-full ">
      <Typography
        variant="H4-med"
        className="text-primary-900 font-family-outfit font-[500] pt-2.5"
      >
        The following volumes match the defined rule:
      </Typography>
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      <div className="py-1 my-1 flex ">
        <div className="relative w-full">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 stroke-[2] text-primary-900 pointer-events-none" />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full min-h-11 pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm text-primary-900 placeholder:text-primary-900 focus:outline-none focus:border-primary-200 focus:ring-1 focus:ring-primary-200"
          />
        </div>
      </div>

      {dryRunMutation.isError && (
        <div className="bg-red/5 border border-red/2 rounded-lg p-4 mb-4">
          <Typography variant="p2" color="text-red">
            Error loading volumes: {dryRunMutation.error?.message || 'Unknown error'}
          </Typography>
        </div>
      )}

      <div className="flex-1 flex flex-col min-h-0 mb-2">
        <VolumesTable
          rowData={currentVolumes}
          pageOffset={pageOffset}
          setPageOffset={setPageOffset}
          data={{ volumes: currentVolumes, count: currentCount }}
          loading={dryRunMutation.isPending}
        />
      </div>
    </div>
  );
}
